import { useState, useEffect, useRef } from "react";
import { ThemeProvider } from "./ThemeProvider";
import "./App.css";

// Components
import Header from "./components/Header";
import LoadingScreen from "./components/LoadingScreen";
import ErrorScreen from "./components/ErrorScreen";
import ChatMessage from "./components/ChatMessage";
import TypingIndicator from "./components/TypingIndicator";
import ChatInput from "./components/ChatInput";
import EmptyState from "./components/EmptyState";
import SettingsModal from "./components/SettingsModal";
import AddMinutesModal from "./components/AddMinutesModal";
import EmailVerificationModal from "./components/Auth/EmailVerificationModal";
import Toast from "./components/Toast";
import Origins, { isAccountVerified, isValidEmail } from "./components/Helpers/Helpers";


//import { log } from "console";
const basename = process.env.REACT_APP_BASENAME || "/";

const App = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [initialQuestions, setInitialQuestions] = useState([]);
  const [showQuestions, setShowQuestions] = useState(false);
  const [error, setError] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAddMinutesOpen, setIsAddMinutesOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const messagesEndRef = useRef(null);
  const [refinedQueries, setRefinedQueries] = useState([]);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [email, setEmail] = useState(localStorage.getItem("email"));
  

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    console.log("Email:", email);
    console.log("showAuthModal:", showAuthModal);
    if (!email || email == '') {
      setShowAuthModal(true);
    }
    else{
      setShowAuthModal(false);
      loadInitialQuestions();
      loadChatHistory();
    }

  }, [email]);

  useEffect(() => {
    // Show questions initially if no messages
    if (messages.length === 0 && initialQuestions.length > 0) {
      setShowQuestions(true);
    }
  }, [messages.length, initialQuestions.length]);

  const showToast = (type, title, message = "", duration = 5000) => {
    setToast({ type, title, message, duration });
  };

  const closeToast = () => {
    setToast(null);
  };

  const handleCopySuccess = () => {
    showToast("success", "Copied!", "Response copied to clipboard");
  };

  const loadChatHistory = () => {
    const savedMessages = sessionStorage.getItem("chatHistory");
    if (savedMessages) {
      const parsedMessages = JSON.parse(savedMessages);
      setMessages(parsedMessages);
    }

    const savedQueries = sessionStorage.getItem("refinedQueries");
    if (savedQueries) {
      const parsedQueries = JSON.parse(savedQueries);
      setRefinedQueries(parsedQueries);
    }
  };

  const saveChatHistory = (newMessages) => {
    sessionStorage.setItem("chatHistory", JSON.stringify(newMessages));
  };

  const saveRefinedQuery = (query) => {
    if (query && !refinedQueries.includes(query)) {
      const newQueries = [...refinedQueries, query].slice(-10); // Keep only last 10
      setRefinedQueries(newQueries);
      sessionStorage.setItem("refinedQueries", JSON.stringify(newQueries));
    }
  };

  const loadInitialQuestions = async () => {
    try {
      setIsInitialLoading(true);
      setError(null);

      const requestBody = {
        env: process.env.REACT_APP_ENV_TYPE
      };

      const response = await fetch(
        "https://apis.metabuddy.app/flaskapp/triage-alerts-base-prompts",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJhdWQiOiIzIiwianRpIjoiNTI1MDkxNGMzMWJhMzJjYTVlZWVkZmUxOGRmYTJmODI0NzBjYmZjOGJjMWNkNzYzYWYxOTdhOGM3YzU0M2EwNGFhYjQ0MzVlMjViNjdjMGQiLCJpYXQiOjE3NDgwODcwMDguNjM5OTU5LCJuYmYiOjE3NDgwODcwMDguNjM5OTYzLCJleHAiOjE3Nzk2MjMwMDguNjI2ODYxLCJzdWIiOiI5MiIsInNjb3BlcyI6W119.ABLwCRB3JqBHDgIqijsOKo4OTx4W_tzjoOlPGhBpFC5NCc6_2mz4dRkSdKlCos6_vyG2m0h1mQlFeHCglwaZ2wTdV7qcf7IfYRu5ZtZMLKTQrLEg-ZuD4ICd6D-t4OEM3oVlLM7QW_VF4WLAXxldMxKshKiBTTPLspAC0eFRGz_XVWMqbl7bevbCTrNgh3DGqp5WyJ9lOO9qNO5Vq6KsaLEEWizj_Wm3FoeRV6S2IcvLCW9gus8PjEQsEYHm2OUhQUKax-rW7g8RTdTrh2eIApT8AlBeDpcrmHmudG-EySWFVsdX_aWffAIatOzowcc4KLBJJEBRXQ1A4F3GQsPQZYXCXuPOdihD6x1vuqUVwc5aN13YJ7rGm7AqlSLEXbqr0RSRAGVfdlV8Ms_OraFHCc4juc7WbQFQ0efLnXphUUWNZgtBc_Lw79m0JuyvM70l8Hcn_fo4OlqhlOfcLTQhrK21Tc0CnXN_JiJtl9H-Y6qNErB2DoCgsXjBz0a5OPUbXQ9WfMcJSMbJJmBV1qAoybQCwSCtlqQLmV-qttVJZ-Ib3orz0ZsQs_k2-bVYwYiXBNB3zki41X0_F2bC4o-oWxiIuk1L6LDFLkcg52EDRkosNWxI7Il-po4QvWU21DojaNLWqP-REfK94W6ZqbklS4wrxCz-BzvaBtRocS4aqKg",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP ${response.status}: ${
            errorText || "Failed to load initial questions"
          }`
        );
      }

      const data = await response.json();

      if (data.status === "success" && data.result?.triage_promots) {
        setInitialQuestions(data.result.triage_promots);
        setError(null);
        /*
        showToast(
          "success",
          "Welcome to Metabuddy AI!",
          "Ready to protect. What do you need help with?"
        );*/
      } else {
        throw new Error(`Invalid response: ${data.message || "Unknown error"}`);
      }
    } catch (err) {
      const errorMessage = `Failed to load questions: ${err.message}`;
      //setError(errorMessage);
      //showToast("error", "Connection Error", err.message);
      console.error("Error loading initial questions:", err);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const sendMessage = async (message) => {
    if (!message.trim() || isLoading) return;

    // Hide questions after first message
    setShowQuestions(false);

    const userMessage = {
      id: Date.now(),
      text: message,
      sender: "user",
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    saveChatHistory(newMessages);
    setInputValue("");
    setIsLoading(true);
    setIsTyping(true);

    try {
      const requestBody = {
        query: message,
        env: process.env.REACT_APP_ENV_TYPE
      };

      const response = await fetch(
        "https://apis.metabuddy.app/flaskapp/search-triage-alerts-weaviate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJhdWQiOiIzIiwianRpIjoiNTI1MDkxNGMzMWJhMzJjYTVlZWVkZmUxOGRmYTJmODI0NzBjYmZjOGJjMWNkNzYzYWYxOTdhOGM3YzU0M2EwNGFhYjQ0MzVlMjViNjdjMGQiLCJpYXQiOjE3NDgwODcwMDguNjM5OTU5LCJuYmYiOjE3NDgwODcwMDguNjM5OTYzLCJleHAiOjE3Nzk2MjMwMDguNjI2ODYxLCJzdWIiOiI5MiIsInNjb3BlcyI6W119.ABLwCRB3JqBHDgIqijsOKo4OTx4W_tzjoOlPGhBpFC5NCc6_2mz4dRkSdKlCos6_vyG2m0h1mQlFeHCglwaZ2wTdV7qcf7IfYRu5ZtZMLKTQrLEg-ZuD4ICd6D-t4OEM3oVlLM7QW_VF4WLAXxldMxKshKiBTTPLspAC0eFRGz_XVWMqbl7bevbCTrNgh3DGqp5WyJ9lOO9qNO5Vq6KsaLEEWizj_Wm3FoeRV6S2IcvLCW9gus8PjEQsEYHm2OUhQUKax-rW7g8RTdTrh2eIApT8AlBeDpcrmHmudG-EySWFVsdX_aWffAIatOzowcc4KLBJJEBRXQ1A4F3GQsPQZYXCXuPOdihD6x1vuqUVwc5aN13YJ7rGm7AqlSLEXbqr0RSRAGVfdlV8Ms_OraFHCc4juc7WbQFQ0efLnXphUUWNZgtBc_Lw79m0JuyvM70l8Hcn_fo4OlqhlOfcLTQhrK21Tc0CnXN_JiJtl9H-Y6qNErB2DoCgsXjBz0a5OPUbXQ9WfMcJSMbJJmBV1qAoybQCwSCtlqQLmV-qttVJZ-Ib3orz0ZsQs_k2-bVYwYiXBNB3zki41X0_F2bC4o-oWxiIuk1L6LDFLkcg52EDRkosNWxI7Il-po4QvWU21DojaNLWqP-REfK94W6ZqbklS4wrxCz-BzvaBtRocS4aqKg",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP ${response.status}: ${errorText || "Failed to get response"}`
        );
      }

      const data = await response.json();

      if (data.status === "success" && data.result) {
        // Save refined query if it exists
        if (data.refined_query) {
          saveRefinedQuery(data.refined_query);
        }

        const botMessage = {
          id: Date.now() + 1,
          text: data.result,
          sender: "bot",
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          refinedQuery: data.refined_query,
        };

        const updatedMessages = [...newMessages, botMessage];
        setMessages(updatedMessages);
        saveChatHistory(updatedMessages);
      } else {
        throw new Error(`Invalid response: ${data.message || "Unknown error"}`);
      }
    } catch (err) {
      const errorMessage = {
        id: Date.now() + 1,
        text: `Sorry, I encountered an error while processing your question: Please try again.`, //${err.message}
        sender: "bot",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isError: true,
        originalQuery: message,
      };
      console.log("Error message:", err.message);
      const updatedMessages = [...newMessages, errorMessage];
      setMessages(updatedMessages);
      saveChatHistory(updatedMessages);
      //showToast("error", "Message Error", err.message);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleAddMinutes = async (minutes) => {
    try {
      showToast(
        "success",
        "Minutes Added!",
        `Successfully logged ${minutes} workout minutes.`
      );
    } catch (error) {
      showToast("error", "Failed to Add Minutes", error.message);
      throw error;
    }
  };

  const handleQuestionClick = (question) => {
    console.log("Question clicked:", question);
    sendMessage(question);
  };

  const handleInputSubmit = () => {
    sendMessage(inputValue);
  };

  const clearChat = () => {
    setMessages([]);
    setRefinedQueries([]);
    setShowQuestions(true);
    sessionStorage.removeItem("chatHistory");
    sessionStorage.removeItem("refinedQueries");
    showToast("info", "Chat Cleared", "Your chat history has been cleared.");
  };

  const retryMessage = (originalQuery) => {
    sendMessage(originalQuery);
  };

  const toggleQuestions = () => {
    setShowQuestions(!showQuestions);
  };

  const openSettings = () => {
    setIsSettingsOpen(true);
  };

  const closeSettings = () => {
    setIsSettingsOpen(false);
  };

  const onSubmit = (val) => {
    setIsSettingsOpen(false);
    showToast(
      "success",
      "Updated!",
      "Updated successfully!"
    );
  };

  const openAddMinutes = () => {
    setIsAddMinutesOpen(true);
  };

  const closeAddMinutes = () => {
    setIsAddMinutesOpen(false);
  };

  const handleAuthSuccess = () => {
    setEmail(localStorage.getItem("email"))
    setShowAuthModal(false);
  };
  const onLogoutHandle = () => {
    setEmail(null);
    setShowAuthModal(true);
  }
  const closeAuthModal = () => {
    setShowAuthModal(false);
  }

  if (isInitialLoading && email) {
    return <LoadingScreen />;
  }

  if (error && initialQuestions.length === 0 && email) {
    return <ErrorScreen error={error} onRetry={loadInitialQuestions} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header
        messages={messages}
        onClearChat={clearChat}
        onOpenSettings={openSettings}
        onOpenAddMinutes={openAddMinutes}
      />

      {/* Chat Container */}
      <div className="pt-16 pb-60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {messages.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  onRetry={retryMessage}
                  onCopySuccess={handleCopySuccess}
                />
              ))}
              {isTyping && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSubmit={handleInputSubmit}
        isLoading={isLoading}
        questions={initialQuestions}
        refinedQueries={refinedQueries}
        onQuestionClick={handleQuestionClick}
        showQuestions={showQuestions}
        onToggleQuestions={toggleQuestions}
        hasMessages={messages.length > 0}
      />

      <SettingsModal isOpen={isSettingsOpen} onClose={closeSettings} onSubmit={onSubmit} onLogout = {onLogoutHandle} />
      <AddMinutesModal
        isOpen={isAddMinutesOpen}
        onClose={closeAddMinutes}
        onSubmit={handleAddMinutes}
      />

      <EmailVerificationModal
        isOpen={showAuthModal}
        onClose={closeAuthModal}
        onSubmit={handleAuthSuccess}
      />
      
      {toast && <Toast toast={toast} onClose={closeToast} />}
    </div>
  );
};

const AppWrapper = () => {
  return (
    <ThemeProvider defaultTheme="system">
      <App />
    </ThemeProvider>
  );
};

export default AppWrapper;
